'use client';

import Image from "next/image";
import Link from "next/link";
import { siteConfig } from "@/lib/siteConfig";
import { Plus, Headset, Mail, Phone, PhoneCall } from "lucide-react";
import GlobalTitle from "@shared/GlobalTitle";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import MailtoLink from "@shared/MailtoLink";

const ANIM_STYLES = `
  @keyframes kenburns {
    0%   { transform: scale(1)    translateX(0)     translateY(0); }
    50%  { transform: scale(1.08) translateX(-1%)   translateY(-1%); }
    100% { transform: scale(1)    translateX(0)     translateY(0); }
  }
  @keyframes floatY {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-10px); }
  }
  @keyframes shimmer {
    0%, 100% { opacity: 0.18; }
    50%       { opacity: 0.38; }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes rotateSlow {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .faq-kenburns   { animation: kenburns 14s ease-in-out infinite; }
  .faq-float      { animation: floatY  5s  ease-in-out infinite; }
  .faq-shimmer    { animation: shimmer 4s  ease-in-out infinite; }
  .faq-slide-up   { animation: slideUp 0.7s cubic-bezier(.22,1,.36,1) both; }
  .faq-rotate     { animation: rotateSlow 18s linear infinite; }
`;

function buildFaqJsonLd(items) {
  const entries = items.filter((item) => typeof item.answer === "string");
  if (!entries.length) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return JSON.stringify(jsonLd).replace(/</g, "\\u003c");
}

export default function FaqSection({
  topText = "Frequently Asked Questions",
  titleText = "Find What You Were",
  coloredText = "Looking For",
  image = "/images/home/FAQ-Background.webp",
  email = siteConfig.email,
  phone = siteConfig.phone,
  items = [],
  contactInfoPosition = "bottom-6",
  bgClass = "bg-background",
  callbackHref = "/contact-us",
}) {
  const faqJsonLd = buildFaqJsonLd(items);

  return (
    <section className={`py-12 md:py-20 ${bgClass}`}>
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: faqJsonLd }}
        />
      )}
      <style>{ANIM_STYLES}</style>
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="flex flex-col gap-6">
            <GlobalTitle
              topText={topText}
              titleText={titleText}
              coloredText={coloredText}
              classes="text-left !mb-0"
            />
            <div className="relative rounded-3xl overflow-hidden flex-1 shadow-2xl shadow-black/20 ring-1 ring-border/40 bg-slate-900">

              {/* Ken Burns image zoom */}
              <div className="absolute inset-0 faq-kenburns">
                <Image
                  src={image}
                  alt="Got Questions?"
                  fill
                  quality={90}
                  sizes="(max-width:1024px) 100vw, 50vw"
                  className="object-cover object-center"
                />
              </div>

              {/* Dark base gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/5" />
              {/* Red brand wash */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/35 via-primary/10 to-transparent" />
              {/* Radial vignette */}
              <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)" }} />

              {/* Animated shimmer orb — top right */}
              <div className="faq-shimmer absolute -top-10 -right-10 w-52 h-52 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(224,39,74,0.35) 0%, transparent 70%)" }} />

              {/* Animated shimmer orb — bottom left */}
              <div className="faq-shimmer absolute -bottom-10 -left-10 w-44 h-44 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(74,124,199,0.25) 0%, transparent 70%)", animationDelay: "2s" }} />

              {/* Contact card — slide up on mount */}
              <div className={`faq-slide-up absolute ${contactInfoPosition} left-6 right-6`} style={{ animationDelay: "0.2s" }}>
                <div className="flex flex-row gap-3 items-center justify-between rounded-2xl backdrop-blur-2xl bg-white/10 border border-white/20 p-5 text-white shadow-[0_8px_32px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.15)]">
                  <div className="flex flex-row gap-3 items-center">
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Headset size={18} />
                    </div>
                    <div className="mb-0">
                      <h3 className="font-bold text-xl leading-tight">
                        Got Questions? We Have Answers
                      </h3>
                      <div className="flex flex-row flex-wrap gap-x-6 gap-y-1.5 text-sm mt-2">
                        <MailtoLink email={email} className="flex items-center gap-2 hover:text-primary transition-colors">
                          <Mail size={14} className="shrink-0" />
                          <span suppressHydrationWarning>{email}</span>
                        </MailtoLink>
                        <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                          <Phone size={14} className="shrink-0" />
                          <span suppressHydrationWarning>{phone}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="relative group shrink-0">
                    <span className="absolute inset-0 rounded-full bg-primary/70 animate-ping pointer-events-none" />
                    <Link
                      href={callbackHref}
                      aria-label="Get a callback"
                      scroll={!callbackHref.startsWith("#")}
                      onClick={(e) => {
                        if (!callbackHref.startsWith("#")) return;
                        e.preventDefault();
                        document
                          .getElementById(callbackHref.slice(1))
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="relative w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-glow-sm hover:shadow-glow-red hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-300 cursor-pointer"
                    >
                      <PhoneCall size={18} />
                    </Link>
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-xs font-medium text-white opacity-0 scale-95 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:scale-100 shadow-lg">
                      Get a Callback
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-ink" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Accordion type="single" collapsible className="flex flex-col gap-4">
            {items.map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="bg-card rounded-2xl border border-border data-[state=open]:border-primary/30 overflow-hidden not-last:border-b transition-colors duration-200"
              >
                <AccordionTrigger className="py-3 px-4 items-center hover:no-underline cursor-pointer **:data-[slot=accordion-trigger-icon]:hidden">
                  <span className="flex-1 text-left font-bold text-ink text-base sm:text-lg">
                    {item.question}
                  </span>
                  <span className="ml-4 shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center transition-colors duration-200 group-aria-expanded/accordion-trigger:bg-red-gradient">
                    <Plus size={20} className="text-primary transition-transform duration-300 group-aria-expanded/accordion-trigger:rotate-45 group-aria-expanded/accordion-trigger:text-white" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-0 text-muted-foreground leading-normal">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
